import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Download } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../components/common';
import NucleusPage from '../../components/nucleus/NucleusPage';
import Inspector, { Band, Facts, Note, useOpen } from '../../components/nucleus/Inspector';
import DataTable from '../../components/nucleus/DataTable';
import { TitledSet } from '../../components/nucleus/SetHead';
import { Row } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import { usePlace } from '../../nucleus/place';
import { dateOf } from '../../nucleus/format';

// BILLING — what you pay for, what you were charged, and what has ended. Every
// amount is the server's own rendering; nothing here does arithmetic on money.
// In a group it is the group's (?group_id=), and its admins' alone: a member
// never sees it. Your own space shows only your own charges.
// when a time was, as a number to sort by (nothing sorts last)
const when = (iso) => (iso ? new Date(iso).getTime() : null);
const appColumn = (of) => ({ id: 'app', label: 'App', lock: true, min: 180, grow: 2, value: of, cell: (r) => of(r) });
const priceColumn = { id: 'price', label: 'Price', min: 120, grow: 1, value: (p) => p.pricing_display?.headline || null, cell: (p) => p.pricing_display?.headline || '—' };

export default function BillingView() {
    const { section, page, to, navigate, groupId, group, isAdmin } = usePlace();
    if (groupId && !isAdmin) {
        return (
            <NucleusPage section={section.label} title={page.label}>
                <Empty>Only an admin of {group?.name || 'this group'} sees its billing.</Empty>
            </NucleusPage>
        );
    }
    return <Billing key={groupId || 'personal'} groupId={groupId} groupName={group?.name || 'this group'} section={section} page={page} to={to} navigate={navigate} />;
}

// WHAT STATE A PURCHASE IS IN — one model for its tag, its actions and its
// facts: a subscription renewing, ending (cancelled at the period's end), whose
// payment failed; a one-off bought outright; or one that has ended.
const isSubscription = (p) => p.pricing_agreed?.model === 'subscription' || !!p.billing_interval;
export function stateOf(p) {
    if (p.status === 'past_due') return 'failed';
    if (p.status !== 'active') return 'ended';
    if (!isSubscription(p)) return 'oneoff';
    return p.cancel_at_period_end ? 'ending' : 'active';
}
const TAG = {
    active: 'Subscription · Active',
    ending: 'Subscription · Ending',
    failed: 'Subscription · Payment failed',
    oneoff: 'One-off · Paid',
    ended: 'Ended',
};
// Stripe's words for a receipt, as a person reads them.
const RECEIPT_WORD = { paid: 'Paid', open: 'Open', uncollectible: 'Failed', void: 'Void', draft: 'Draft' };
export const receiptWord = (s) => RECEIPT_WORD[s] || (s ? `${s[0].toUpperCase()}${s.slice(1).replace(/_/g, ' ')}` : '');
// How an ended purchase ended, in a word.
const ENDED_WORD = { canceled: 'Cancelled', refunded: 'Refunded', pending: 'Not completed' };
const endedWord = (s) => ENDED_WORD[s] || (s ? `${s[0].toUpperCase()}${s.slice(1).replace(/_/g, ' ')}` : 'Ended');
const MONEY_OFF = 'Payments aren’t set up on this instance';
const UNPAID = ['open', 'uncollectible'];

// A RECEIPT'S DOCUMENTS ARE STRIPE'S, on Stripe's own pages: an outside http(s)
// address opens in a new tab, and anything else (a relative or same-origin
// path, another scheme) is no address at all — never handed to the router.
export function externalUrl(u) {
    if (!u || typeof u !== 'string') return null;
    try {
        const url = new URL(u);
        if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) return null;
        return url.href;
    } catch {
        return null;
    }
}
const newTab = (u) => () => { window.open(u, '_blank', 'noopener'); };

function Billing({ groupId, groupName, section, page, to, navigate }) {
    const toast = useToast();
    const [state, setState] = useState(null); // null loading · { purchases, invoices, paymentsOn } · { off }
    const [openId, open, close] = useOpen();

    const load = useCallback(async () => {
        const q = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
        try {
            const [p, i] = await Promise.all([
                groupId ? api.request(`/billing/purchases${q}`) : api.listPurchases(),
                (groupId ? api.request(`/billing/invoices${q}`) : api.listInvoices()).catch(() => ({ invoices: [] })),
            ]);
            setState({ purchases: p?.purchases || [], invoices: i?.invoices || [], paymentsOn: p?.payments_enabled !== false });
        } catch (e) {
            if (e?.status === 503 || /not configured|not enabled/i.test(e?.message || '')) setState({ off: true });
            else setState({ purchases: [], invoices: [], paymentsOn: true, error: e?.message || 'Could not read billing.' });
        }
    }, [groupId]);
    useEffect(() => { load(); }, [load]);

    const purchases = state?.purchases || [];
    const invoices = state?.invoices || [];
    const live = purchases.filter((p) => ['active', 'past_due'].includes(p.status));
    const ended = purchases.filter((p) => !['active', 'past_due'].includes(p.status));
    // ?open= holds a purchase or a receipt: their ids never collide.
    const current = purchases.find((p) => String(p.id) === String(openId));
    const receipt = !current && invoices.find((inv) => String(inv.id) === String(openId));
    const receiptsOf = (p) => invoices.filter((inv) => String(inv.purchase_id) === String(p.id));

    const until = (p) => (p.current_period_end ? dateOf(p.current_period_end) : 'the end of the period paid for');
    // EVERY ACT THAT MOVES MONEY is held back, saying why, where this instance
    // takes no payments — rather than offered and then refused.
    const money = (a) => (state?.paymentsOn === false ? { ...a, disabled: true, hint: MONEY_OFF } : a);
    const lead = (a) => a && { ...a, primary: true };
    // A PAST-DUE PURCHASE LEADS WITH ITS CARD: the app is paused until a payment
    // goes through. A subscription can also be cancelled to end with the period
    // paid for — separate from disconnecting the app, which ends its access (and
    // the subscription) now — and an ending one resumed.
    const actionsOf = (p) => {
        const app = p.app_id && { id: 'app', label: 'Open in Apps', run: () => navigate(to('apps', null, `open=${p.app_id}`)) };
        const card = money({
            id: 'card', label: 'Update payment method',
            ...(!p.stripe_customer_id ? { disabled: true, hint: 'No card is on file for this purchase' } : {}),
            run: async () => {
                const r = await api.updatePaymentMethod(p.id, window.location.href);
                if (r?.url) window.location.href = r.url;
            },
        });
        const cancel = money({
            id: 'cancel', label: 'Cancel subscription', danger: true,
            confirm: {
                title: 'Cancel this subscription?',
                body: `${p.app_name || 'The app'} keeps its access until ${until(p)}, the end of the period paid for, and is not charged again. To end its access now, disconnect it instead.`,
                label: 'Cancel subscription',
            },
            run: async () => {
                const r = await api.cancelSubscription(p.id);
                toast.success(`Cancelled. ${p.app_name || 'The app'} keeps its access until ${r?.access_until ? dateOf(r.access_until) : until(p)}.`);
                load();
            },
        });
        const resume = money({
            id: 'resume', label: 'Resume subscription',
            run: async () => {
                await api.resumeSubscription(p.id);
                toast.success(`Resumed. ${p.app_name || 'The app'} renews ${p.current_period_end ? `on ${dateOf(p.current_period_end)}` : 'as before'}.`);
                load();
            },
        });
        const refund = p.refundable && money({
            id: 'refund', label: 'Refund', danger: true,
            confirm: {
                title: 'Refund this purchase?',
                body: `Refund ${p.pricing_display?.headline || 'it'} for ${p.app_name || 'this app'}? The app loses access at the same time. No reason needed.`,
                label: 'Refund',
            },
            run: async () => {
                const res = await api.refundPurchase(p.id);
                toast.success(res?.message || 'Refunded.');
                close();
                load();
            },
        });
        const again = p.app_id && money({ id: 'again', label: 'Buy again', run: () => navigate(to('apps', 'store', `open=${p.app_id}`)) });
        return {
            active: [lead(app), card, cancel, refund],
            ending: [lead(resume), app],
            failed: [lead(card), app],
            oneoff: [lead(app), refund],
            ended: [lead(app), again],
        }[stateOf(p)].filter(Boolean);
    };

    // What the purchase says of itself, by its state.
    const factsOf = (p) => {
        const s = stateOf(p);
        const due = receiptsOf(p).find((inv) => ['open', 'uncollectible'].includes(inv.status));
        return [
            { label: 'Plan', value: p.pricing_display?.headline },
            { label: 'Started', value: dateOf(p.started_at || p.paid_at || p.created_at) || undefined },
            groupId && p.paid_by?.name && { label: 'Bought by', value: p.paid_by.name },
            s === 'active' && p.current_period_end && { label: 'Next charge', value: dateOf(p.current_period_end) },
            s === 'ending' && { label: 'Access until', value: until(p) },
            s === 'ended' && (p.canceled_at || p.current_period_end) && { label: 'Ended on', value: dateOf(p.canceled_at || p.current_period_end) },
            s === 'failed' && { label: 'Payment due', value: dateOf(due?.issued_at || p.current_period_end) || 'Now', tone: 'live' },
            p.paid_so_far && { label: 'Paid so far', value: p.paid_so_far.display },
        ];
    };

    // Where a live purchase stands, in words: its Status cell.
    const standingOf = (p) => {
        const s = stateOf(p);
        if (s === 'failed') return 'Payment failed · the app is paused';
        if (s === 'ending') return `Ends ${until(p)} · cancelled`;
        if (s === 'oneoff') return `Paid once ${dateOf(p.started_at || p.paid_at || p.created_at) || ''}`.trim();
        return 'Active';
    };

    const toReceipts = () => document.getElementById('billing-receipts')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

    const purchasePanel = (p) => {
        const s = stateOf(p);
        const its = receiptsOf(p);
        return (
            <Inspector
                key={p.id}
                tag={TAG[s]}
                title={p.app_name || 'An app'}
                sub={p.pricing_display?.headline}
                actions={actionsOf(p)}
                onClose={close}
            >
                <Band title="This purchase">
                    <Facts items={factsOf(p)} />
                    {s === 'failed' && <Note tone="live">The payment failed, so the app is paused until it goes through. Update the payment method and it is tried again.</Note>}
                    {s === 'ending' && <Note>{`Cancelled: it keeps access until ${until(p)} and is not charged again.`}</Note>}
                    {p.refundable && <Note>{`Refundable for ${p.refund_window_days} days after buying, with no reason asked.`}</Note>}
                    {p.is_grandfathered && (
                        <Note>{groupId ? `${groupName} is` : 'You are'} on the price {groupId ? 'it' : 'you'} signed up at.{p.app_pricing_now_display?.headline ? ` It is now ${p.app_pricing_now_display.headline} for new users.` : ''}</Note>
                    )}
                    {isSubscription(p) && s !== 'ended' && <Note>Disconnecting the app ends its access and cancels the subscription at once.</Note>}
                </Band>
                <Band title="Its receipts" aside={<button type="button" className="nx-link" onClick={toReceipts}>All receipts</button>}>
                    {its.length ? its.map((inv) => (
                        <Row
                            key={inv.id}
                            name={[inv.issued_at && dateOf(inv.issued_at), inv.amount_display, receiptWord(inv.status)].filter(Boolean).join(' · ')}
                            onPress={() => open(inv.id)}
                        />
                    )) : <Note>Nothing has been charged for it yet.</Note>}
                </Band>
            </Inspector>
        );
    };

    const receiptActionsOf = (inv) => {
        const doc = externalUrl(inv.hosted_invoice_url);
        const pdf = externalUrl(inv.invoice_pdf);
        const unpaid = UNPAID.includes(inv.status);
        return [
            unpaid && money({
                id: 'pay', label: 'Pay now', primary: true, icon: <CreditCard size={14} strokeWidth={1.8} aria-hidden="true" />,
                ...(doc ? {} : { disabled: true, hint: 'Stripe gave no page to pay this on' }),
                run: newTab(doc),
            }),
            {
                id: 'open-link', label: 'Open invoice', primary: !unpaid,
                ...(doc ? {} : { disabled: true, hint: 'Stripe gave no invoice page for this receipt' }),
                run: newTab(doc),
            },
            {
                id: 'pdf', label: 'Download PDF', icon: <Download size={14} strokeWidth={1.8} aria-hidden="true" />,
                ...(pdf ? {} : { disabled: true, hint: 'Stripe gave no PDF for this receipt' }),
                run: newTab(pdf),
            },
        ].filter(Boolean);
    };

    const receiptPanel = (inv) => {
        const its = inv.purchase_id && purchases.find((p) => String(p.id) === String(inv.purchase_id));
        const period = [inv.period_start, inv.period_end].map((t) => dateOf(t)).filter(Boolean).join(' – ');
        return (
            <Inspector
                key={inv.id}
                tag={`Receipt · ${receiptWord(inv.status)}`}
                title={`${inv.app_name || 'A charge'} · ${inv.amount_display}`}
                sub={dateOf(inv.issued_at) || undefined}
                actions={receiptActionsOf(inv)}
                onClose={close}
            >
                <Band title="This receipt">
                    <Facts items={[
                        { label: 'Amount', value: inv.amount_display },
                        { label: 'Date', value: dateOf(inv.issued_at) || undefined },
                        { label: 'Period', value: period || undefined },
                        { label: 'Status', value: receiptWord(inv.status), tone: UNPAID.includes(inv.status) ? 'live' : undefined },
                        inv.purchase_id && {
                            label: 'Purchase',
                            value: its ? <button type="button" className="nx-link" onClick={() => open(its.id)}>{its.app_name || 'Its purchase'}</button> : undefined,
                        },
                    ]} />
                    {UNPAID.includes(inv.status) && <Note tone="live">This charge has not gone through. Pay it on Stripe’s page.</Note>}
                </Band>
            </Inspector>
        );
    };

    // EACH TABLE SAYS WHERE ITS ROWS STAND: a failed payment (the app waits
    // until it goes through), an ending subscription and its last day, a one-off
    // and when it was paid. Only what will be charged again has a next charge;
    // an ended purchase says how it ended, and when.
    const tinted = (tone, words) => (tone ? <span className={`nx-dt__tone is-${tone}`}>{words}</span> : words);
    const liveColumns = [
        appColumn((p) => p.app_name || 'An app'),
        priceColumn,
        { id: 'status', label: 'Status', min: 120, grow: 1.4, value: (p) => stateOf(p),
          cell: (p) => tinted(stateOf(p) === 'failed' && 'live', standingOf(p)) },
        { id: 'next', label: 'Next charge', min: 110, grow: 0, asc: true,
          value: (p) => (['active', 'failed'].includes(stateOf(p)) ? when(p.current_period_end) : null),
          cell: (p) => (['active', 'failed'].includes(stateOf(p)) && p.current_period_end ? dateOf(p.current_period_end) : '—') },
    ];
    // a receipt opens in the inspector, as a purchase does
    const receiptColumns = [
        appColumn((inv) => inv.app_name || 'A charge'),
        { id: 'amount', label: 'Amount', num: true, min: 100, grow: 0, value: (inv) => inv.amount_paid ?? null, cell: (inv) => inv.amount_display || '—' },
        { id: 'date', label: 'Date', min: 110, grow: 0, value: (inv) => when(inv.issued_at), cell: (inv) => (inv.issued_at ? dateOf(inv.issued_at) : '—') },
        { id: 'status', label: 'Status', min: 90, grow: 1, value: (inv) => inv.status, cell: (inv) => tinted(inv.status !== 'paid' && 'warning', receiptWord(inv.status)) },
    ];
    const endedOf = (p) => p.canceled_at || p.current_period_end;
    const endedColumns = [
        appColumn((p) => p.app_name || 'An app'),
        priceColumn,
        { id: 'status', label: 'Status', min: 110, grow: 1, value: (p) => endedWord(p.status), cell: (p) => endedWord(p.status) },
        { id: 'ended', label: 'Ended on', min: 110, grow: 0, value: (p) => when(endedOf(p)), cell: (p) => (endedOf(p) ? dateOf(endedOf(p)) : '—') },
    ];

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            inspector={current ? purchasePanel(current) : receipt ? receiptPanel(receipt) : null}
        >
            {state === null ? null : state.off ? (
                <Empty>Payments are not enabled on this Paradigm instance. Every app here is free.</Empty>
            ) : (
                <>
                    {state.error && <Empty error>{state.error}</Empty>}
                    <div className="nx-shelf" style={{ paddingTop: 24, paddingBottom: 48, gap: 40 }}>
                        <TitledSet
                            title={groupId ? `What ${groupName} pays for` : 'What you pay for'}
                            count={live.length}
                            empty={groupId ? `${groupName} is not paying for any apps. Most apps are free.` : 'You are not paying for any apps. Most apps are free.'}
                        >
                            {live.length > 0 && (
                                <DataTable
                                    id="billing-subscriptions" label="Subscriptions" columns={liveColumns} rows={live} keyOf={(p) => p.id}
                                    currentKey={openId} onOpen={open} actionsOf={actionsOf}
                                />
                            )}
                        </TitledSet>
                        <div id="billing-receipts">
                            <TitledSet title={groupId ? `${groupName}’s receipts` : 'Your receipts'} count={state.invoices.length} empty={groupId ? `Nothing has been charged to ${groupName} yet.` : 'Nothing has been charged to you yet.'}>
                                {/* a receipt opens here, in the inspector; its invoice
                                    and PDF are Stripe's, opened from there */}
                                {state.invoices.length > 0 && (
                                    <DataTable
                                        id="billing-receipts-table" label="Receipts" columns={receiptColumns} rows={state.invoices} keyOf={(inv) => inv.id}
                                        sort={{ id: 'date', dir: 'desc' }} currentKey={openId} onOpen={open}
                                    />
                                )}
                            </TitledSet>
                        </div>
                        {ended.length > 0 && (
                            <TitledSet title="Ended" count={ended.length}>
                                <DataTable id="billing-ended" label="Ended" columns={endedColumns} rows={ended} keyOf={(p) => p.id} currentKey={openId} onOpen={open} />
                            </TitledSet>
                        )}
                    </div>
                </>
            )}
        </NucleusPage>
    );
}
